// Webhook subscriptions (specs 30 §3): an app's (Settings › Apps) or the
// store's own (Settings › Notifications), the signing key under the list,
// the delivery log, resend and test sends.
import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Copy, Eye, EyeOff, KeyRound, MoreHorizontal, RefreshCw, Webhook } from "lucide-react";
import { api, ApiError, isAuthPrompt, storePath } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { useStore } from "../../lib/store";
import { topicGroup, topicKey, useApiCatalog, type Catalog, type Delivery, type Subscription } from "../../lib/apps";
import { MenuButton } from "../../components/FilterPill";
import { Picker } from "../../components/Picker";
import { Badge, Banner, Button, Card, confirmDialog, cx, Field, fieldError, Input, Modal, Skeleton, useToast } from "../../components/ui";
import type { Key } from "../../locales/en";

// CopyValue: a read-only value in a field-styled box with a copy button.
// secret: masked until shown.
export function CopyValue({ value, secret, mono = true }: { value: string; secret?: boolean; mono?: boolean }) {
  const { t } = useI18n();
  const [done, setDone] = useState(false);
  const [shown, setShown] = useState(!secret);
  return (
    <div className="flex h-[38px] min-w-0 items-center rounded-md border border-line-strong bg-subtle ps-3">
      <code className={cx("min-w-0 flex-1 truncate text-[13px] text-ink", mono && "font-mono")} dir="ltr">
        {shown ? value : "•".repeat(Math.min(value.length, 32))}
      </code>
      {secret && (
        <button type="button" aria-label={t(shown ? "apps.hide" : "apps.reveal")} onClick={() => setShown(!shown)}
          className="flex h-full items-center px-2 text-ink-muted hover:text-ink">{shown ? <EyeOff className="size-4" /> : <Eye className="size-4" />}</button>
      )}
      <button type="button" aria-label={t("common.copy")} className="flex h-full items-center rounded-e-md border-s border-line-strong px-2.5 text-ink-muted hover:bg-white hover:text-ink"
        onClick={() => { void navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); }}>
        {done ? <Check className="size-4 text-success" /> : <Copy className="size-4" />}
      </button>
    </div>
  );
}

const splitList = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);

export function useTopicLabel() {
  const { t } = useI18n();
  return (name: string) => (t(topicKey(name) as Key) === topicKey(name) ? name : t(topicKey(name) as Key));
}

function useGroupLabel() {
  const { t } = useI18n();
  return (g: string) => (g === "store" ? t("wh.group.store") : t(`apps.scope.${g}` as Key));
}

type SigningKey = { key: string; created_at: string; rotated_at: string | null };

// WebhookApp: the app whose webhooks the card manages (none: the store's own).
export type WebhookApp = { id: string; name: string; kind: "custom" | "partner" };

export function WebhooksCard({ app, disabled, onShowCredentials }: { app?: WebhookApp; disabled?: boolean; onShowCredentials?: () => void }) {
  const appId = app?.id;
  const { t, relative } = useI18n();
  const { id, can } = useStore();
  const qc = useQueryClient();
  const toast = useToast();
  const catalog = useApiCatalog(id);
  const topicLabel = useTopicLabel();
  const key = ["store", id, "/webhooks", appId ?? "store"];
  const q = useQuery({ queryKey: key, enabled: !!id,
    queryFn: () => api<{ data: Subscription[] }>(storePath(id!, appId ? `/webhooks?app_id=${appId}` : "/webhooks")) });
  // Staff see every subscription without app_id: the store's own are those without an app.
  const subs = (q.data?.data ?? []).filter((w) => (appId ? w.app_id === appId : !w.app_id));
  const [editing, setEditing] = useState<Subscription | "new" | null>(null);
  const [logOf, setLogOf] = useState<Subscription | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: key });
  const onError = (e: Error) => { if (!isAuthPrompt(e)) toast(e.message, true); };
  const setActive = useMutation({
    mutationFn: ({ w, on }: { w: Subscription; on: boolean }) => api(storePath(id!, `/webhooks/${w.id}/${on ? "enable" : "disable"}`), { method: "POST" }),
    onSuccess: refresh, onError,
  });
  const remove = useMutation({
    mutationFn: (w: Subscription) => api(storePath(id!, `/webhooks/${w.id}`), { method: "DELETE" }),
    onSuccess: () => { toast(t("wh.deleted")); refresh(); }, onError,
  });
  const test = useMutation({
    mutationFn: (w: Subscription) => api<Delivery>(storePath(id!, `/webhooks/${w.id}/test`), { method: "POST" }),
    onSuccess: (d) => d.success ? toast(t("wh.test_sent", { status: String(d.status_code ?? "") }))
      : toast(t("wh.test_failed", { error: d.error || String(d.status_code ?? t("wh.no_response")) }), true),
    onError,
  });
  const edit = !disabled && (appId ? can("apps.manage") : can("settings.notifications"));
  const create = edit && catalog.data ? <Button size="sm" onClick={() => setEditing("new")}>{t("wh.add")}</Button> : undefined;
  return (
    <Card title={t("wh.title")} description={appId ? t("wh.app_hint") : t("wh.store_hint")} actions={subs.length > 0 ? create : undefined}>
      {q.isPending ? <Skeleton /> : subs.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-line-strong px-4 py-8 text-center">
          <span className="flex size-10 items-center justify-center rounded-full bg-brand-tint text-brand"><Webhook className="size-5" strokeWidth={1.75} /></span>
          <p className="text-sm font-medium text-ink">{t("wh.empty")}</p>
          <p className="max-w-sm text-[13px] text-ink-muted">{t("wh.empty_body")}</p>
          {create && <div className="pt-1">{create}</div>}
        </div>
      ) : (
        <div className="-mx-5 -mt-1 overflow-x-auto border-y border-line">
          <table className="data-table w-full text-sm">
            <thead>
              <tr>
                <th className="px-5 text-start">{t("wh.topic")}</th>
                <th className="px-3 text-start">{t("wh.endpoint")}</th>
                <th className="px-3 text-start">{t("wh.status")}</th>
                <th className="w-12 px-3"><span className="sr-only">{t("wh.actions")}</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {subs.map((w) => (
                <tr key={w.id}>
                  <td className="px-5 py-2.5 align-middle">
                    <button type="button" className="text-start" onClick={() => setLogOf(w)}>
                      <span className="block font-medium text-ink hover:underline">{topicLabel(w.topic)}</span>
                      <span className="block font-mono text-xs text-ink-muted" dir="ltr">{w.topic}</span>
                    </button>
                  </td>
                  <td className="max-w-[18rem] px-3 py-2.5"><span className="block truncate font-mono text-xs text-ink-muted" dir="ltr" title={w.endpoint}>{w.endpoint}</span></td>
                  <td className="px-3 py-2.5">
                    <Badge tone={w.status === "disabled" ? "critical" : w.failure_since ? "warning" : "success"}>
                      {w.status === "disabled" ? t("wh.status.disabled") : w.failure_since ? t("wh.status.failing") : t("wh.status.active")}
                    </Badge>
                    {w.status === "disabled" && w.disabled_at && <span className="mt-1 block text-xs text-ink-muted">{t("wh.disabled_on", { date: relative(w.disabled_at) })}</span>}
                    {w.status === "active" && w.failure_since && <span className="mt-1 block text-xs text-ink-muted">{t("wh.failing_since", { date: relative(w.failure_since) })}</span>}
                  </td>
                  <td className="px-3 py-2.5 text-end">
                    <MenuButton label={<MoreHorizontal className="size-4" aria-label={t("wh.actions")} />}>
                      {(close) => (
                        <div className="flex flex-col text-sm">
                          <MenuItem onClick={() => { close(); setLogOf(w); }}>{t("wh.deliveries")}</MenuItem>
                          {edit && <>
                            <MenuItem onClick={() => { close(); test.mutate(w); }}>{t("wh.test")}</MenuItem>
                            <MenuItem onClick={() => { close(); setEditing(w); }}>{t("wh.edit")}</MenuItem>
                            <MenuItem onClick={() => { close(); setActive.mutate({ w, on: w.status !== "active" }); }}>{t(w.status === "active" ? "wh.disable" : "wh.enable")}</MenuItem>
                            <div className="my-1 border-t border-line" />
                            <MenuItem danger onClick={async () => {
                              close();
                              if (await confirmDialog({ title: t("wh.delete_title"), body: t("wh.delete_body", { endpoint: w.endpoint }), tone: "critical", confirmLabel: t("common.delete") })) remove.mutate(w);
                            }}>{t("common.delete")}</MenuItem>
                          </>}
                        </div>
                      )}
                    </MenuButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {app ? <AppSigning app={app} onShowCredentials={onShowCredentials} docsURL={catalog.data?.docs_url} />
        : can("settings.notifications") && <StoreSigningKey docsURL={catalog.data?.docs_url} />}
      {editing && catalog.data && <SubscriptionSheet catalog={catalog.data} appId={appId} sub={editing === "new" ? undefined : editing}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); toast(t("wh.saved")); refresh(); }} />}
      {logOf && <DeliveriesSheet sub={logOf} canResend={edit} onClose={() => setLogOf(null)} />}
    </Card>
  );
}

// SigningPanel: the one key that signs the webhooks listed above, named
// as such, so it's never mistaken for an API token or the client ID.
function SigningPanel({ body, children, footer }: { body: ReactNode; children?: ReactNode; footer?: ReactNode }) {
  const { t } = useI18n();
  return (
    <section aria-label={t("wh.key_title")} className="mt-5 rounded-lg border border-line bg-subtle/60 p-4">
      <div className="flex min-w-0 items-start gap-3">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-white text-ink-muted ring-1 ring-line"><KeyRound className="size-4" /></span>
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink">{t("wh.key_title")}
            <span dir="ltr" className="rounded bg-white px-1.5 py-0.5 font-mono text-[11px] font-normal text-ink-muted ring-1 ring-line">X-Sooquna-Hmac-Sha256</span></p>
          <p className="mt-0.5 text-[13px] text-ink-muted">{body}</p>
        </div>
      </div>
      {children && <div className="mt-3 sm:ps-11">{children}</div>}
      {footer && <p className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-muted sm:ps-11">{footer}</p>}
    </section>
  );
}

// StoreSigningKey: the store's own webhooks ("Your webhooks will be signed
// with …", like Sooquna), with rotation.
function StoreSigningKey({ docsURL }: { docsURL?: string }) {
  const { t, date } = useI18n();
  const { id } = useStore();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["store", id, "/webhooks/signing-key"];
  const q = useQuery({ queryKey: key, enabled: !!id, queryFn: () => api<SigningKey>(storePath(id!, "/webhooks/signing-key")) });
  const rotate = useMutation({
    mutationFn: () => api<SigningKey>(storePath(id!, "/webhooks/signing-key/rotate"), { method: "POST" }),
    onSuccess: (k) => { qc.setQueryData(key, k); toast(t("wh.key_rotated")); },
    onError: (e) => { if (!isAuthPrompt(e)) toast(e.message, true); },
  });
  return (
    <SigningPanel body={t("wh.key_store_body")}
      footer={<>
        {q.data && <span>{q.data.rotated_at ? t("wh.key_rotated_on", { date: date(q.data.rotated_at) }) : t("wh.key_created_on", { date: date(q.data.created_at) })}</span>}
        {docsURL && <a href={docsURL} target="_blank" rel="noreferrer" className="text-brand hover:underline">{t("wh.verify_docs")}</a>}
      </>}>
      {q.isPending ? <Skeleton lines={1} /> : q.isError ? <Banner tone="critical">{q.error.message}</Banner> : (
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-0 flex-1 basis-64"><CopyValue value={q.data.key} secret /></div>
          <Button onClick={async () => {
            if (await confirmDialog({ title: t("wh.rotate_title"), body: t("wh.rotate_body"), confirmLabel: t("wh.rotate"), tone: "critical" })) rotate.mutate();
          }} loading={rotate.isPending}><RefreshCw className="size-3.5" />{t("wh.rotate")}</Button>
        </div>
      )}
    </SigningPanel>
  );
}

// AppSigning: an app's webhooks are signed with its client secret. A
// custom app's is revealed right here; a connected app's stays with its
// developer, who verifies the webhooks.
function AppSigning({ app, onShowCredentials, docsURL }: { app: WebhookApp; onShowCredentials?: () => void; docsURL?: string }) {
  const { t } = useI18n();
  const { id } = useStore();
  const toast = useToast();
  const reveal = useMutation({
    mutationFn: () => api<{ client_secret: string }>(storePath(id!, `/apps/${app.id}/credentials`), { body: { rotate: false } }),
    onError: (e) => { if (!isAuthPrompt(e)) toast(e.message, true); },
  });
  if (app.kind === "partner") return <SigningPanel body={t("wh.key_partner_body", { app: app.name })} />;
  return (
    <SigningPanel body={t("wh.key_app_body")}
      footer={<>
        {onShowCredentials && <button type="button" className="text-brand hover:underline" onClick={onShowCredentials}>{t("wh.key_rotate_in_credentials")}</button>}
        {docsURL && <a href={docsURL} target="_blank" rel="noreferrer" className="text-brand hover:underline">{t("wh.verify_docs")}</a>}
      </>}>
      {reveal.data ? <CopyValue value={reveal.data.client_secret} secret /> : (
        <div className="flex h-[38px] items-center justify-between rounded-md border border-line-strong bg-white ps-3">
          <span className="font-mono text-[13px] text-ink-subtle" dir="ltr">••••••••••••••••••••••••</span>
          <button type="button" className="h-full rounded-e-md border-s border-line-strong px-3 text-[13px] font-medium text-ink hover:bg-subtle disabled:opacity-50"
            disabled={reveal.isPending} onClick={() => reveal.mutate()}>{t("apps.reveal")}</button>
        </div>
      )}
    </SigningPanel>
  );
}

function MenuItem({ children, onClick, danger }: { children: React.ReactNode; onClick: () => void; danger?: boolean }) {
  return <button type="button" role="menuitem" onClick={onClick} className={cx("rounded-md px-2.5 py-1.5 text-start hover:bg-subtle", danger && "text-danger")}>{children}</button>;
}

export function VersionPicker({ catalog, value, onChange, id, disabled }: { catalog: Catalog; value: string; onChange: (v: string) => void; id?: string; disabled?: boolean }) {
  const { t, date } = useI18n();
  return (
    <Picker id={id} value={value} onChange={onChange} disabled={disabled} options={catalog.versions.map((v) => ({
      value: v.name, mono: true, label: v.name,
      description: v.name === catalog.current ? t("apps.version_latest") : t("apps.version_until", { date: date(v.sunset) }),
    }))} />
  );
}

function SubscriptionSheet({ catalog, appId, sub, onClose, onSaved }: { catalog: Catalog; appId?: string; sub?: Subscription; onClose: () => void; onSaved: () => void }) {
  const { t } = useI18n();
  const { id } = useStore();
  const topicLabel = useTopicLabel();
  const groupLabel = useGroupLabel();
  const [f, setF] = useState({
    topic: sub?.topic ?? "orders/create", endpoint: sub?.endpoint ?? "", api_version: sub?.api_version ?? catalog.current,
    fields: (sub?.fields ?? []).join(", "), namespaces: (sub?.metafield_namespaces ?? []).join(", "),
  });
  const [advanced, setAdvanced] = useState(!!(sub?.fields.length || sub?.metafield_namespaces.length));
  const topics = useMemo(() => {
    const order = [...new Set(catalog.topics.map(topicGroup))];
    return [...catalog.topics].sort((a, b) => order.indexOf(topicGroup(a)) - order.indexOf(topicGroup(b)))
      .map((tp) => ({ value: tp.name, label: topicLabel(tp.name), text: `${topicLabel(tp.name)} ${tp.name}`, group: topicGroup(tp),
        description: <span dir="ltr" className="font-mono">{tp.name}</span> }));
  }, [catalog.topics, topicLabel]);
  const save = useMutation({
    mutationFn: () => {
      const body = { app_id: appId ?? null, topic: f.topic, endpoint: f.endpoint.trim(), api_version: f.api_version,
        fields: splitList(f.fields), metafield_namespaces: splitList(f.namespaces) };
      return sub ? api(storePath(id!, `/webhooks/${sub.id}`), { method: "PUT", body: { ...body, version: sub.version } })
        : api(storePath(id!, "/webhooks"), { body });
    },
    onSuccess: onSaved,
  });
  const err = save.error;
  return (
    <Modal open variant="sheet" title={sub ? t("wh.edit") : t("wh.add")} onClose={onClose}
      footer={<><Button onClick={onClose}>{t("common.cancel")}</Button><Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>{t("common.save")}</Button></>}>
      <div className="space-y-5">
        {err && !(err instanceof ApiError && err.errors.length) && !isAuthPrompt(err) && <Banner tone="critical">{err.message}</Banner>}
        <div className="space-y-1">
          <span className="text-sm font-medium text-ink">{t("wh.topic")}</span>
          <Picker value={f.topic} options={topics} onChange={(topic) => setF({ ...f, topic })} groupLabel={groupLabel}
            searchPlaceholder={t("wh.search_topics")} invalid={!!fieldError(err, "topic")} />
          {fieldError(err, "topic") ? <span className="block text-xs text-danger">{fieldError(err, "topic")}</span>
            : <span className="block text-xs text-ink-muted">{t("wh.topic_hint")}</span>}
        </div>
        <Field label={t("wh.endpoint")} hint={t("wh.endpoint_hint")} error={fieldError(err, "endpoint")}>
          <Input dir="ltr" type="url" inputMode="url" placeholder="https://example.com/webhooks" value={f.endpoint} onChange={(e) => setF({ ...f, endpoint: e.target.value })} />
        </Field>
        <div className="space-y-1">
          <span className="text-sm font-medium text-ink">{t("apps.api_version")}</span>
          <VersionPicker catalog={catalog} value={f.api_version} onChange={(api_version) => setF({ ...f, api_version })} />
          <span className="block text-xs text-ink-muted">{t("wh.version_hint")}</span>
        </div>
        <div className="rounded-lg border border-line">
          <button type="button" aria-expanded={advanced} onClick={() => setAdvanced(!advanced)}
            className="flex w-full items-center justify-between rounded-lg px-4 py-3 text-sm font-medium text-ink hover:bg-subtle">
            {t("wh.advanced")}<ChevronDown className={cx("size-4 text-ink-muted transition-transform", advanced && "rotate-180")} />
          </button>
          {advanced && (
            <div className="space-y-4 border-t border-line p-4">
              <Field label={t("wh.fields")} hint={t("wh.fields_hint")} optional error={fieldError(err, "fields")}>
                <Input dir="ltr" placeholder="id, name, total_price" value={f.fields} onChange={(e) => setF({ ...f, fields: e.target.value })} />
              </Field>
              <Field label={t("wh.namespaces")} hint={t("wh.namespaces_hint")} optional error={fieldError(err, "metafield_namespaces")}>
                <Input dir="ltr" placeholder="custom" value={f.namespaces} onChange={(e) => setF({ ...f, namespaces: e.target.value })} />
              </Field>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function DeliveriesSheet({ sub, canResend, onClose }: { sub: Subscription; canResend: boolean; onClose: () => void }) {
  const { t, date } = useI18n();
  const { id } = useStore();
  const toast = useToast();
  const topicLabel = useTopicLabel();
  const key = ["store", id, "/webhooks", sub.id, "deliveries"];
  const q = useQuery({ queryKey: key, enabled: !!id, queryFn: () => api<{ data: Delivery[] }>(storePath(id!, `/webhooks/${sub.id}/deliveries?limit=100`)) });
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const resend = useMutation({
    mutationFn: (d: Delivery) => api<Delivery>(storePath(id!, `/webhooks/${sub.id}/deliveries/${d.webhook_id}/resend`), { method: "POST" }),
    onSuccess: (d) => {
      toast(d.success ? t("wh.resent", { status: String(d.status_code ?? "") }) : t("wh.test_failed", { error: d.error || String(d.status_code ?? t("wh.no_response")) }), !d.success);
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => { if (!isAuthPrompt(e)) toast(e.message, true); },
  });
  const list = q.data?.data ?? [];
  const ok = list.filter((d) => d.success).length;
  return (
    <Modal open variant="sheet" title={topicLabel(sub.topic)} onClose={onClose} footer={<Button onClick={onClose}>{t("apps.done")}</Button>}>
      <div className="space-y-4">
        <div className="space-y-1 text-[13px]">
          <p className="break-all font-mono text-ink-muted" dir="ltr">{sub.topic} → {sub.endpoint}</p>
          <p className="text-ink-muted">{t("wh.log_hint")}</p>
        </div>
        {list.length > 0 && <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-line p-3"><p className="text-xs text-ink-muted">{t("wh.delivered")}</p><p className="mt-0.5 text-lg font-semibold tabular-nums">{ok}</p></div>
          <div className="rounded-lg border border-line p-3"><p className="text-xs text-ink-muted">{t("wh.failed")}</p><p className={cx("mt-0.5 text-lg font-semibold tabular-nums", list.length - ok > 0 && "text-danger")}>{list.length - ok}</p></div>
        </div>}
        {q.isPending ? <Skeleton lines={5} /> : list.length === 0 ? <p className="rounded-lg border border-dashed border-line-strong px-4 py-8 text-center text-sm text-ink-muted">{t("wh.log_empty")}</p> : (
          <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line">
            {list.map((d) => (
              <li key={d.id}>
                <button type="button" className="flex w-full items-center gap-3 px-3 py-2.5 text-start text-sm hover:bg-subtle" aria-expanded={open === d.id}
                  onClick={() => setOpen(open === d.id ? null : d.id)}>
                  <Badge tone={d.success ? "success" : "critical"}>{d.status_code ?? t("wh.no_response")}</Badge>
                  <span className="min-w-0 flex-1">
                    <span className="block text-ink">{date(d.created_at, { dateStyle: "medium", timeStyle: "medium" })}</span>
                    <span className="block text-xs text-ink-muted">{t("wh.attempt", { n: d.attempt })}{d.manual && <> · {t("wh.manual")}</>} · {t("wh.latency", { ms: d.latency_ms })}</span>
                  </span>
                  <ChevronDown className={cx("size-4 shrink-0 text-ink-muted transition-transform", open === d.id && "rotate-180")} />
                </button>
                {open === d.id && (
                  <div className="space-y-3 border-t border-line bg-subtle/50 px-3 py-3 text-xs">
                    {d.error && <Banner tone="critical">{d.error}</Banner>}
                    <Block title={t("wh.request")}>{Object.entries(d.request_headers ?? {}).map(([k, v]) => `${k}: ${v}`).join("\n")}{d.request_body ? "\n\n" + pretty(d.request_body) : ""}</Block>
                    <Block title={t("wh.response")}>{d.response_body || "—"}</Block>
                    {canResend && <Button size="sm" onClick={() => resend.mutate(d)} loading={resend.isPending && resend.variables?.id === d.id}><RefreshCw className="size-3.5" />{t("wh.resend")}</Button>}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 font-medium text-ink">{title}</p>
      <pre dir="ltr" className="max-h-56 overflow-auto whitespace-pre-wrap break-all rounded-md border border-line bg-white p-2.5 font-mono text-[12px] leading-5 text-ink">{children}</pre>
    </div>
  );
}

function pretty(body: string) {
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
}
